import React, { FC, ReactElement, useMemo, useState } from 'react';
import classnames from 'classnames';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { animateScroll } from 'react-scroll';
import { toast } from 'react-toastify';
import { mixed, number, object, string } from 'yup';

import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import CompensationForm, {
  calculateTotal
} from '@buyside/components/presentation/CompensationSubmissionPage/compensationForm';
import SegmentedSelector from '@buyside/components/presentation/SegmentedSelector/segmentedSelector';

import { useIsMobile, BREAKPOINTS } from '@buyside/util/hooks/useIsMobile';
import { userCompensationsUrl } from '@buyside/util/urlHelper';

import {
  CompensationFieldsFragment,
  CompensationSubmissionCompensationDocument,
  CompensationSubmissionCompensationQuery,
  CreateOrUpdateCompensationMutationVariables,
  Maybe,
  useCompensationSubmissionCompensationQuery,
  useCreateOrUpdateCompensationMutation
} from '@interfaces/graphql-typings';

import styles from './compensationSubmissionPageStyles.scss';

const currentYear = new Date().getFullYear();

const schema = object().shape({
  title: string().required('This field is required'),
  experience: string().required('This field is required'),
  industry: string().required('This field is required'),
  location: string().required('This field is required'),
  fundType: string().required('This field is required'),
  fundStrategy: string().required('This field is required'),
  fundSize: string().required('This field is required'),
  assetClass: string().required('This field is required'),
  baseSalary: number()
    .transform(val => (isNaN(val) || val === 0 ? undefined : val))
    .required('This field is required'),
  annualDiscrBonus: mixed().notRequired(),
  interestIncentiveFeesValue: mixed().notRequired(),
  interestMgmtFeesValue: mixed().notRequired(),
  comments: mixed().when(
    ['baseSalary', 'annualDiscrBonus', 'interestIncentiveFeesValue', 'interestMgmtFeesValue'],
    {
      is: (baseSlary, annualDiscrBonus, interestIncentiveFeesValue, interestMgmtFeesValue) =>
        baseSlary &&
        calculateTotal([
          baseSlary,
          annualDiscrBonus,
          interestIncentiveFeesValue,
          interestMgmtFeesValue
        ]) < 100000,
      then: string().required('Please explain your situation.'),
      otherwise: mixed().notRequired()
    }
  )
});

const generateFormValues = (
  compensation?: Maybe<CompensationFieldsFragment>
): CreateOrUpdateCompensationMutationVariables => {
  const {
    status,
    category,
    year,
    title,
    experience,
    industry,
    location,
    fundType,
    fundStrategy,
    fundSize,
    assetClass,
    baseSalary,
    interestMgmtFeesPerc = 0,
    interestMgmtFeesValue = 0,
    vestingMgmtFees = 0,
    interestIncentiveFeesPerc = 0,
    interestIncentiveFeesValue = 0,
    vestingIncentiveFees = 0,
    annualDiscrBonus = 0,
    totalDeferredComp = 0,
    investInFund = false,
    buildPersonalTrackRecord = false,
    marketPersonalTrackRecord = false,
    comments
  } = compensation || {};
  return {
    status: status || 'draft',
    category: category || 'actual',
    year: year || currentYear - 1,
    title,
    experience,
    industry,
    location,
    fundType,
    fundStrategy,
    fundSize,
    assetClass,
    baseSalary,
    interestMgmtFeesPerc,
    interestMgmtFeesValue,
    vestingMgmtFees,
    interestIncentiveFeesPerc,
    interestIncentiveFeesValue,
    vestingIncentiveFees,
    annualDiscrBonus,
    totalDeferredComp,
    investInFund,
    buildPersonalTrackRecord,
    marketPersonalTrackRecord,
    comments
  };
};

const CompensationSubmission: FC<{ actualId?: Maybe<string>; estimatedId?: Maybe<string> }> = ({
  actualId,
  estimatedId
}): ReactElement => {
  const [currentStep, setCurrentStep] = useState<number>(0);

  const [
    createOrUpdateCompensation,
    { data: createdData, loading }
  ] = useCreateOrUpdateCompensationMutation({
    update: (proxy, { data: createdData }) => {
      const updatedCompensation = createdData?.createOrUpdateCompensation?.compensation;
      if (!updatedCompensation) return;

      const prevRes = proxy.readQuery<CompensationSubmissionCompensationQuery>({
        query: CompensationSubmissionCompensationDocument,
        variables: { actualId: updatedCompensation.id, estimatedId }
      });
      if (!prevRes) return;

      proxy.writeQuery({
        query: CompensationSubmissionCompensationDocument,
        data: {
          actualCompensation: updatedCompensation,
          estimatedCompensation: prevRes.estimatedCompensation
        }
      });
    },
    onError: error => {
      toast.error(error.message, { theme: 'colored' });
    }
  });

  const { data, loading: queryLoading } = useCompensationSubmissionCompensationQuery({
    variables: { actualId, estimatedId }
  });

  const actualCompensation =
    data?.actualCompensation || createdData?.createOrUpdateCompensation?.compensation;
  const estimatedCompensation = data?.estimatedCompensation;

  const steps = useMemo(
    () => [
      { label: 'Actual', name: 'actual', year: actualCompensation?.year || currentYear - 1 },
      {
        label: 'Estimated',
        name: 'estimated',
        year: actualCompensation?.year ? actualCompensation.year + 1 : currentYear
      }
    ],
    [actualCompensation?.year]
  );

  const formMethods = useForm<CreateOrUpdateCompensationMutationVariables>({
    defaultValues: generateFormValues(),
    mode: 'onChange',
    resolver: yupResolver(schema),
    values: generateFormValues(currentStep === 1 ? estimatedCompensation : actualCompensation)
  });
  const {
    formState: { isValid },
    getValues,
    handleSubmit,
    reset,
    trigger
  } = formMethods;

  const createOrUpdate = (variables: CreateOrUpdateCompensationMutationVariables): void => {
    createOrUpdateCompensation({ variables }).then(({ data }) => {
      if (currentStep === 0) {
        reset({
          ...generateFormValues(estimatedCompensation),
          category: steps[1].name,
          year: steps[1].year
        });
        // Update URL without reloading after the actual comp is first created
        if (window.location.pathname.includes('/new')) {
          history.replaceState(
            null,
            '',
            `${data?.createOrUpdateCompensation?.compensation?.id}/edit`
          );
        }
        setCurrentStep(1);
        animateScroll.scrollToTop({ duration: 1000 });
      } else if (currentStep === 1) {
        window.location.assign(userCompensationsUrl());
      }
    });
  };

  const onSubmit = (status: 'draft' | 'published'): void => {
    let variables = { ...getValues(), status };
    if (currentStep === 0 && actualCompensation) {
      variables = { ...variables, id: actualCompensation.id };
    } else if (currentStep === 1 && estimatedCompensation) {
      variables = { ...variables, id: estimatedCompensation.id };
    }
    // Skip form validation unless published
    status === 'published'
      ? handleSubmit(() => createOrUpdate(variables))()
      : createOrUpdate(variables);
  };

  const isMobile = useIsMobile(BREAKPOINTS.SMALLISH);

  const selectorOptions = useMemo(
    () =>
      steps.map((step, index) => {
        let completed = false;
        if (index === 0) {
          completed = Boolean(actualCompensation?.completed);
        } else if (index === 1) {
          completed = Boolean(estimatedCompensation?.completed);
        }

        return {
          label: (
            <span>
              <FontAwesomeIcon
                className={classnames(styles.selectorIcon, {
                  [styles.selectorIconComplete]: completed
                })}
                icon={faCheck}
              />
              <>
                {isMobile ? (
                  <>
                    {step.year} {step.label}
                  </>
                ) : (
                  <>
                    {step.label} Compensation for {step.year}
                  </>
                )}
              </>
            </span>
          ),
          value: index
        };
      }),
    [actualCompensation?.completed, estimatedCompensation?.completed, isMobile, steps]
  );

  return (
    <div className={styles.researchFormWizard}>
      <div className={styles.researchFormWizardHeader}>
        <h4>Submit Compensation</h4>
      </div>
      <FormProvider {...formMethods}>
        <div className={styles.researchFormWizardSteps}>
          <SegmentedSelector<number>
            disabled={loading || queryLoading}
            onChange={setCurrentStep}
            options={selectorOptions}
            selected={currentStep}
          />
        </div>
        <div className={classnames(styles.researchFormWizardSection, styles.wizardSecOverride)}>
          {currentStep === 1 && actualCompensation ? (
            <div className={styles.checkboxWrapper}>
              <input
                id="cbx-46"
                className={styles.inpCbx}
                defaultChecked={false}
                onChange={e => {
                  e.target.checked
                    ? reset({
                        ...generateFormValues(actualCompensation),
                        category: steps[1].name,
                        year: steps[1].year
                      })
                    : reset({
                        ...generateFormValues(estimatedCompensation),
                        category: steps[1].name,
                        year: steps[1].year
                      });
                  trigger();
                }}
                type="checkbox"
              />
              <label className={styles.cbx} htmlFor="cbx-46">
                <span>
                  <svg width="12px" height="10px" viewBox="0 0 12 10">
                    <polyline points="1.5 6 4.5 9 10.5 1"></polyline>
                  </svg>
                </span>
                <span className={styles.cbxLabel} data-testid="copy-prior-year">
                  Copy {currentYear - 1} data
                </span>
              </label>
            </div>
          ) : null}
          <CompensationForm disabled={loading || queryLoading} />
        </div>
        <div className={styles.researchFormWizardSubmissionButtons}>
          <Button
            buttonType={BUTTON_TYPES.BUTTON}
            isLoading={loading || queryLoading}
            type={BUTTON_TYPES.PRIMARY}
            text={currentStep === 0 ? 'Save & Next' : 'Save as Draft'}
            onClick={() => onSubmit('draft')}
          />
          {currentStep === 1 ? (
            <>
              <Button
                disabled={!actualCompensation?.completed || !isValid || loading || queryLoading}
                isLoading={loading || queryLoading}
                type={BUTTON_TYPES.PRIMARY}
                text="Publish"
                onClick={() => onSubmit('published')}
              />
              {!loading && isValid && !actualCompensation?.completed ? (
                <span className={styles.actualIncompleteMsg}>
                  Please complete the {currentYear - 1} data before publishing.
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      </FormProvider>
    </div>
  );
};

export default CompensationSubmission;
